import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Percent, Sparkles, Clock, Loader2, RefreshCw, Save, X, Search as SearchIcon,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function pct(rate) { return `${(Number(rate || 0) * 100).toFixed(rate < 0.01 ? 2 : 1)}%`; }
function fmtDate(iso) { return iso ? new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : "—"; }

function StatusPill({ status, daysLeft }) {
  if (status.source === "override") {
    return <span className="rounded-full bg-copper/10 px-2 py-0.5 text-[11px] font-semibold text-copper">Manual override</span>;
  }
  if (status.is_in_free_period) {
    return (
      <span className="rounded-full bg-forest/10 px-2 py-0.5 text-[11px] font-semibold text-forest" title={`Free until ${fmtDate(status.free_until)}`}>
        Promo · {daysLeft} days left
      </span>
    );
  }
  return <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-700">Standard</span>;
}

export default function AdminHostCommissions() {
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({ total: 0, active_promo: 0, standard_rate: 0.10, free_period_days: 90 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ rate: "", free_days_from_now: "", note: "" });

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${API}/admin/hosts/commissions`);
      setItems(data.hosts || []);
      setMeta({
        total: data.total,
        active_promo: data.active_promo,
        standard_rate: data.standard_rate,
        free_period_days: data.free_period_days,
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load host commissions");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    if (!filter.trim()) return items;
    const q = filter.trim().toLowerCase();
    return items.filter((h) =>
      (h.email || "").toLowerCase().includes(q) ||
      (h.name || "").toLowerCase().includes(q)
    );
  }, [items, filter]);

  const startEdit = (h) => {
    setEditing(h.user_id);
    setForm({
      rate: h.override_rate !== null && h.override_rate !== undefined ? String(Math.round(h.override_rate * 10000) / 100) : "",
      free_days_from_now: "",
      note: h.override_note || "",
    });
  };
  const cancelEdit = () => { setEditing(null); setForm({ rate: "", free_days_from_now: "", note: "" }); };

  const submitOverride = async (h, opts = {}) => {
    const payload = {};
    if (opts.clearRate) {
      payload.rate = null;
    } else if (form.rate !== "") {
      const parsed = parseFloat(form.rate);
      if (Number.isNaN(parsed) || parsed < 0 || parsed > 100) return toast.error("Rate must be 0–100 (percent)");
      payload.rate = parsed / 100;
    }
    if (form.free_days_from_now !== "") {
      const d = parseInt(form.free_days_from_now, 10);
      if (Number.isNaN(d) || d < 0) return toast.error("Free days must be ≥ 0");
      payload.free_days_from_now = d;
    }
    if (form.note !== "") payload.note = form.note;
    try {
      setSaving(true);
      await axios.patch(`${API}/admin/hosts/${h.user_id}/commission`, payload);
      toast.success(
        opts.clearRate ? `Override cleared for ${h.email}` : `Commission updated for ${h.email}`
      );
      cancelEdit();
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6" data-testid="admin-host-commissions">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500">Hosts</div>
          <h1 className="mt-1 font-display text-3xl font-bold text-slate-900">Host commissions</h1>
          <p className="mt-2 max-w-2xl text-sm text-slate-600">
            Every new host gets a promotional <b>0 % commission</b> for their first {meta.free_period_days} days.
            After that they automatically flip to the {pct(meta.standard_rate)} standard rate — no action needed.
            You can override any host&apos;s rate or extend their free window from here.
          </p>
        </div>
        <div className="flex flex-wrap gap-3">
          <div className="rounded-2xl border border-forest/20 bg-forest/5 px-4 py-3 text-right" data-testid="promo-count-card">
            <div className="text-[10px] font-bold uppercase tracking-widest text-forest">In promo window</div>
            <div className="mt-1 font-display text-2xl font-bold text-forest">{meta.active_promo}</div>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-right">
            <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Standard rate</div>
            <div className="mt-1 font-display text-2xl font-bold text-slate-900">{pct(meta.standard_rate)}</div>
          </div>
        </div>
      </div>

      <div className="mb-3 flex items-center gap-2">
        <div className="flex flex-1 items-center gap-2 rounded-full border border-slate-200 bg-white px-3">
          <SearchIcon className="h-4 w-4 text-slate-400" />
          <input
            data-testid="host-search-input"
            type="text"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Filter hosts by email or name"
            className="w-full bg-transparent py-2 text-sm outline-none"
          />
        </div>
        <button onClick={load} data-testid="hosts-refresh"
          className="flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-600 hover:border-copper hover:text-copper">
          <RefreshCw className="h-3.5 w-3.5" /> Refresh
        </button>
      </div>

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white card-shadow">
        <table className="w-full text-sm" data-testid="hosts-commissions-table">
          <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-widest text-slate-500">
            <tr>
              <th className="px-4 py-3">Host</th>
              <th className="px-4 py-3">Became host</th>
              <th className="px-4 py-3">Free period expires</th>
              <th className="px-4 py-3">Current rate</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading ? (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-sm text-slate-500">
                <Loader2 className="mr-2 inline h-4 w-4 animate-spin" /> Loading hosts…
              </td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-sm text-slate-400">
                {filter ? "No hosts match that filter." : "No hosts yet."}
              </td></tr>
            ) : filtered.map((h) => (
              <React.Fragment key={h.user_id}>
                <tr data-testid={`host-row-${h.email}`}>
                  <td className="px-4 py-3">
                    <div className="font-semibold text-slate-800">{h.name || "—"}</div>
                    <div className="text-xs text-slate-500">{h.email}</div>
                    {h.is_verified && (
                      <span className="mt-1 inline-block rounded-full bg-forest/10 px-2 py-0.5 text-[10px] font-semibold text-forest">
                        Verified
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-700">{fmtDate(h.became_host_at)}</td>
                  <td className="px-4 py-3 text-sm text-slate-700">
                    {h.free_until ? (
                      <>
                        {fmtDate(h.free_until)}
                        {h.is_in_free_period && h.days_left !== null && (
                          <div className="text-[11px] text-forest">
                            <Clock className="mr-0.5 inline h-3 w-3" />
                            {h.days_left} days left
                          </div>
                        )}
                      </>
                    ) : "—"}
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-display text-base font-bold text-slate-900" data-testid={`host-rate-${h.email}`}>
                      {pct(h.rate)}
                    </div>
                    {h.override_rate !== null && h.override_rate !== undefined && (
                      <div className="text-[11px] text-copper">Overridden</div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <StatusPill status={h} daysLeft={h.days_left} />
                  </td>
                  <td className="px-4 py-3 text-right">
                    {editing === h.user_id ? (
                      <button onClick={cancelEdit} data-testid={`cancel-edit-${h.email}`} className="text-xs text-slate-500 hover:text-slate-800">
                        Cancel
                      </button>
                    ) : (
                      <button onClick={() => startEdit(h)} data-testid={`edit-commission-${h.email}`} className="text-xs font-semibold text-copper hover:underline">
                        <Percent className="mr-1 inline h-3 w-3" /> Override
                      </button>
                    )}
                  </td>
                </tr>
                {editing === h.user_id && (
                  <tr data-testid={`edit-row-${h.email}`}>
                    <td colSpan={6} className="bg-slate-50 px-4 py-4">
                      <div className="grid gap-3 sm:grid-cols-4">
                        <div>
                          <label className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Manual rate %</label>
                          <input
                            data-testid={`override-rate-${h.email}`}
                            type="number" min={0} max={100} step={0.1}
                            value={form.rate}
                            onChange={(e) => setForm((f) => ({ ...f, rate: e.target.value }))}
                            placeholder={`Leave blank to auto (${pct(meta.standard_rate)})`}
                            className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-copper"
                          />
                        </div>
                        <div>
                          <label className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Extend free window (days)</label>
                          <input
                            data-testid={`override-days-${h.email}`}
                            type="number" min={0} max={3650}
                            value={form.free_days_from_now}
                            onChange={(e) => setForm((f) => ({ ...f, free_days_from_now: e.target.value }))}
                            placeholder="e.g. 30"
                            className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-copper"
                          />
                        </div>
                        <div className="sm:col-span-2">
                          <label className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Note (internal)</label>
                          <input
                            data-testid={`override-note-${h.email}`}
                            type="text"
                            value={form.note}
                            onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
                            placeholder="Reason for the manual rate"
                            className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-copper"
                          />
                        </div>
                      </div>
                      <div className="mt-3 flex flex-wrap items-center justify-end gap-2">
                        {h.override_rate !== null && h.override_rate !== undefined && (
                          <button
                            onClick={() => submitOverride(h, { clearRate: true })}
                            data-testid={`clear-override-${h.email}`}
                            disabled={saving}
                            className="flex items-center gap-1 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 hover:border-rose-400 hover:text-rose-600 disabled:opacity-50"
                          >
                            <X className="h-3 w-3" /> Clear override
                          </button>
                        )}
                        <button
                          onClick={() => submitOverride(h)}
                          data-testid={`save-override-${h.email}`}
                          disabled={saving}
                          className="btn-primary flex items-center gap-1 text-xs !py-1.5 !px-3 disabled:opacity-50"
                        >
                          {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
                          Save
                        </button>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
